import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  getRelationships,
  createRelationship,
  updateRelationship,
  deleteRelationship,
  type CreateRelationshipDto,
  type UpdateRelationshipDto,
} from '@/api/relationships.api';
import { ApiError } from '@/api/client';

// ─── Beziehungen laden ──────────────────────────────────────────────────────

/**
 * Lädt Beziehungen – optional nach Person gefiltert.
 * @param personId - Wenn gesetzt, nur Beziehungen dieser Person
 */
export function useRelationships(personId?: string) {
  return useQuery({
    queryKey: personId
      ? QUERY_KEYS.relationships.byPerson(personId)
      : QUERY_KEYS.relationships.all,
    queryFn: () => getRelationships(personId),
  });
}

// ─── Beziehung erstellen ────────────────────────────────────────────────────

export function useCreateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateRelationshipDto) => createRelationship(dto),
    onSuccess: (rel) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.relationships.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      // Person-Details aktualisieren (Partnerschaften-Tab)
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(rel.person1Id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(rel.person2Id) });

      const name1 = `${rel.person1.firstName} ${rel.person1.lastName ?? ''}`.trim();
      const name2 = `${rel.person2.firstName} ${rel.person2.lastName ?? ''}`.trim();
      toast.success(`Beziehung zwischen ${name1} und ${name2} erstellt.`);
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Beziehung konnte nicht erstellt werden.');
    },
  });
}

// ─── Beziehung aktualisieren ────────────────────────────────────────────────

export function useUpdateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateRelationshipDto }) =>
      updateRelationship(id, dto),
    onSuccess: (rel) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.relationships.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(rel.person1Id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(rel.person2Id) });
      toast.success('Beziehung aktualisiert.');
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Beziehung konnte nicht aktualisiert werden.');
    },
  });
}

// ─── Beziehung löschen ──────────────────────────────────────────────────────

export function useDeleteRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteRelationship(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.relationships.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      toast.success('Beziehung gelöscht.');
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Beziehung konnte nicht gelöscht werden.');
    },
  });
}
